import { useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { ToastAction } from "@/components/Toast/Toast"
import { useToast } from "@/components/Toast"
import {
  useCommsContacts,
  useSetContactRelevance,
  formatPurgeCountdown,
  CommsContactRow,
  formatAge,
  DOCS_CONTACTS_BASE_URL,
} from "@/hooks/useComms"
import { UsersIcon } from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

function RelevanceBadge({ value }: { value: boolean | null }) {
  if (value === true)
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        relevant
      </span>
    )
  if (value === false)
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
        not relevant
      </span>
    )
  return (
    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">
      unclassified
    </span>
  )
}

type View = "active" | "bucket"

export function CommsContacts() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [view, setView] = useState<View>("active")
  const { data, isLoading, error } = useCommsContacts({
    limit: 200,
    ...(view === "bucket" ? { bucket: "not_relevant" as const } : {}),
  })
  const setRelevance = useSetContactRelevance()

  // Pending "Not relevant" confirmation (active view → quarantine).
  const [pending, setPending] = useState<CommsContactRow | null>(null)

  const contactLabel = (c: CommsContactRow) => c.display_name || c.primary_email

  const markNotRelevant = (contact: CommsContactRow) => {
    setRelevance.mutate(
      { publicId: contact.public_id, relevant: false },
      {
        onSuccess: () => {
          toast({
            title: "Marked not relevant",
            description: `${contactLabel(contact)} and all their mail are now hidden.`,
            action: (
              <ToastAction
                altText="Restore this contact"
                onClick={() =>
                  setRelevance.mutate({ publicId: contact.public_id, relevant: true })
                }
              >
                Undo
              </ToastAction>
            ),
          })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark not relevant. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const restore = (contact: CommsContactRow) => {
    setRelevance.mutate(
      { publicId: contact.public_id, relevant: true },
      {
        onSuccess: () => {
          toast({
            title: "Restored",
            description: `${contactLabel(contact)} is back in the active view.`,
          })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to restore. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const activeColumns = useMemo<ColumnDef<CommsContactRow>[]>(
    () => [
      {
        accessorKey: "display_name",
        header: "Name",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="font-medium text-primary">
              {row.original.display_name || row.original.primary_email}
            </p>
            <p className="truncate text-xs text-muted-foreground">{row.original.primary_email}</p>
          </div>
        ),
      },
      {
        accessorKey: "org",
        header: "Org",
        cell: ({ row }) =>
          row.original.org_ref ? (
            <Link
              to={`/comms/orgs/${row.original.org_ref.public_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-blue-600 hover:underline"
            >
              {row.original.org_ref.name}
            </Link>
          ) : row.original.org ? (
            <span className="text-sm">{row.original.org}</span>
          ) : (
            <span className="text-xs italic text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "last_contacted_at",
        header: "Last contacted",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{formatAge(row.original.last_contacted_at)}</span>
        ),
      },
      {
        id: "awaiting",
        header: "Awaiting",
        cell: ({ row }) =>
          row.original.awaiting_count > 0 ? (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              {row.original.awaiting_count} owed
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: "relevance",
        header: "Relevance",
        cell: ({ row }) => <RelevanceBadge value={row.original.is_relevant} />,
      },
      {
        accessorKey: "doc_match_status",
        header: "Doc",
        cell: ({ row }) =>
          row.original.doc_slug ? (
            <a
              href={`${DOCS_CONTACTS_BASE_URL}/${row.original.doc_slug}.md`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-blue-600 hover:underline"
            >
              {row.original.doc_slug}
            </a>
          ) : (
            <span className="text-xs italic text-muted-foreground">{row.original.doc_match_status}</span>
          ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              setPending(row.original)
            }}
          >
            Not relevant
          </Button>
        ),
      },
    ],
    []
  )

  const bucketColumns = useMemo<ColumnDef<CommsContactRow>[]>(
    () => [
      {
        accessorKey: "display_name",
        header: "Name",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="font-medium text-primary">
              {row.original.display_name || row.original.primary_email}
            </p>
            <p className="truncate text-xs text-muted-foreground">{row.original.primary_email}</p>
          </div>
        ),
      },
      {
        accessorKey: "org",
        header: "Org",
        cell: ({ row }) =>
          row.original.org_ref ? (
            <Link
              to={`/comms/orgs/${row.original.org_ref.public_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-blue-600 hover:underline"
            >
              {row.original.org_ref.name}
            </Link>
          ) : row.original.org ? (
            <span className="text-sm">{row.original.org}</span>
          ) : (
            <span className="text-xs italic text-muted-foreground">—</span>
          ),
      },
      {
        id: "purge",
        header: "Retention",
        // A row reaches the bucket because its OWN flag is not-relevant (a 30-day
        // purge clock runs) OR only because its ORG is not-relevant (resolved at
        // read time — not_relevant_at is null and the body is NEVER purged). Don't
        // show a false purge countdown for the org-hidden case.
        cell: ({ row }) =>
          row.original.is_relevant === false ? (
            <span className="text-xs text-muted-foreground">
              {formatPurgeCountdown(row.original.not_relevant_at)}
            </span>
          ) : (
            <span className="text-xs italic text-muted-foreground">
              hidden via org{row.original.org_ref ? ` · ${row.original.org_ref.name}` : ""} — kept
            </span>
          ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              restore(row.original)
            }}
          >
            Restore
          </Button>
        ),
      },
    ],
    // restore is stable for the page's lifetime (mutation ref); the rule would
    // force adding it but it never changes identity meaningfully here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const contacts = data?.contacts ?? []
  const total = data?.total ?? contacts.length
  // The table search is client-side over the loaded page; if more contacts exist
  // than were loaded, say so rather than implying the list is complete.
  const subtitle =
    view === "bucket"
      ? isLoading
        ? "Loading…"
        : `${total} not relevant · individually-marked contacts purge 30 days after marking; org-hidden ones are kept until the org is restored`
      : isLoading
        ? "Loading…"
        : total > contacts.length
          ? `Showing first ${contacts.length} of ${total} — search covers the loaded rows`
          : `${total} people you've emailed`

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Comms · Contacts</h1>
            <p className="text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        {/* Active vs. "Not relevant" bucket toggle */}
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setView("active")}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              view === "active"
                ? "bg-primary text-contrast"
                : "text-muted-foreground hover:text-primary"
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setView("bucket")}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              view === "bucket"
                ? "bg-primary text-contrast"
                : "text-muted-foreground hover:text-primary"
            }`}
          >
            Not relevant
          </button>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="h-12 w-12" />}
            title={view === "bucket" ? "Nothing quarantined" : "No contacts yet"}
            description={
              view === "bucket"
                ? "Contacts you mark “Not relevant” land here until they’re purged."
                : "Captured email contacts will appear here."
            }
          />
        ) : (
          <DataTable
            columns={view === "bucket" ? bucketColumns : activeColumns}
            data={contacts}
            searchKey="display_name"
            onRowClick={(row) => navigate(`/comms/contacts/${row.public_id}`)}
          />
        )}
      </div>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title="Mark contact not relevant?"
        description={
          pending
            ? `${contactLabel(pending)} and all their mail (existing and future) will be hidden from every comms view and queued for purge in 30 days. You can undo this or restore them from the “Not relevant” bucket.`
            : ""
        }
        confirmLabel="Not relevant"
        confirmVariant="destructive"
        onConfirm={() => {
          if (pending) markNotRelevant(pending)
        }}
      />
    </Layout>
  )
}
